/**
 * Pure validation for the Broker support notice contacts returned by
 * GET /users/support-contact. Anything that is not a plainly safe value is
 * dropped so it can never reach a mailto:/tel: href or the rendered text.
 */

const MAX_EMAIL_LENGTH = 254;
const MAX_PHONE_LENGTH = 32;

// Stricter than the backend on purpose: no `%`, `?`, `&`, `#`, `:` or
// whitespace, so a value cannot decode into extra mailto headers or a body.
const SAFE_EMAIL_RE = /^[A-Za-z0-9._+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;
// Mirrors the backend support-phone rule.
const SAFE_PHONE_RE = /^\+?[0-9][0-9 ()-]{5,30}[0-9]$/;

export const SUPPORT_NEUTRAL_MESSAGE = 'Need assistance? Please contact your service provider.';
export const SUPPORT_LOADING_MESSAGE = 'Need assistance? Loading support details…';
export const SUPPORT_CONTACT_PREFIX = 'Need assistance? Contact Customer Support';

export function normalizeSupportEmail(value) {
  if (typeof value !== 'string') return null;
  const candidate = value.trim();
  if (!candidate || candidate.length > MAX_EMAIL_LENGTH) return null;
  return SAFE_EMAIL_RE.test(candidate) ? candidate : null;
}

export function normalizeSupportPhone(value) {
  if (typeof value !== 'string') return null;
  const candidate = value.trim();
  if (!candidate || candidate.length > MAX_PHONE_LENGTH) return null;
  return SAFE_PHONE_RE.test(candidate) ? candidate : null;
}

export function buildMailtoHref(email) {
  const safe = normalizeSupportEmail(email);
  return safe ? `mailto:${safe}` : null;
}

// Keeps a leading `+` only when the number already has one; separators are
// stripped and a `+` is never added.
export function buildTelHref(phone) {
  const safe = normalizeSupportPhone(phone);
  if (!safe) return null;
  const prefix = safe.startsWith('+') ? '+' : '';
  return `tel:${prefix}${safe.replace(/[^0-9]/g, '')}`;
}

/**
 * Collapse a support-contact query into what the notice may render.
 * An error always wins so a failed refetch can never keep showing earlier data.
 */
export function resolveSupportContact({ data, isLoading = false, isError = false } = {}) {
  if (isError) return { status: 'none', email: null, phone: null };
  if (isLoading) return { status: 'loading', email: null, phone: null };
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { status: 'none', email: null, phone: null };
  }
  const email = normalizeSupportEmail(data.support_email);
  const phone = normalizeSupportPhone(data.support_phone);
  if (!email && !phone) return { status: 'none', email: null, phone: null };
  return { status: 'ready', email, phone };
}
